import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid2 as Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Stack,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import {
  Save as SaveIcon,
  Psychology as PromptIcon,
  SmartToy as AiIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { CreateChatPromptDto, AiModelProvider } from "@/dto/ChatPromptDto";
import { Yn } from "@/constants/code";

/**
 * 프롬프트 생성 폼 Props
 */
export interface CreateFormProps {
  /**
   * 제출 핸들러
   */
  onSubmit: (data: CreateChatPromptDto) => void;

  /**
   * 로딩 상태
   */
  isLoading?: boolean;
}

/**
 * 프롬프트 생성 폼 컴포넌트
 * @description QNA 채팅 프롬프트를 생성하는 폼입니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const CreateForm = ({
  onSubmit,
  isLoading = false,
}: CreateFormProps) => {
  const [formData, setFormData] = useState<CreateChatPromptDto>({
    promptKey: "",
    promptContent: "",
    aiModelProvider: "OPENAI",
    aiModelName: "gpt-4",
    aiModelTemperature: 0.7,
    aiMaxTokens: 4000,
    useCondition: "",
    priorityOrder: 1,
    useYn: Yn.Y,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // 폼 검증
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.promptKey?.trim()) {
      newErrors.promptKey = "프롬프트 키는 필수입니다.";
    } else if (formData.promptKey.length > 50) {
      newErrors.promptKey = "프롬프트 키는 50자 이하여야 합니다.";
    }

    if (!formData.promptContent?.trim()) {
      newErrors.promptContent = "프롬프트 내용은 필수입니다.";
    }

    if (formData.aiModelTemperature < 0 || formData.aiModelTemperature > 2) {
      newErrors.aiModelTemperature = "온도는 0.0 ~ 2.0 사이여야 합니다.";
    }

    if (formData.aiMaxTokens < 1 || formData.aiMaxTokens > 32000) {
      newErrors.aiMaxTokens = "최대 토큰은 1 ~ 32,000 사이여야 합니다.";
    }

    if (formData.priorityOrder < 1) {
      newErrors.priorityOrder = "우선순위는 1 이상이어야 합니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 제출 핸들러
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  // 입력값 변경 핸들러
  const handleChange = (field: keyof CreateChatPromptDto, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // 에러 제거
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // AI 모델명 옵션
  const getModelOptions = () => {
    switch (formData.aiModelProvider) {
      case "OPENAI":
        return [
          { value: "gpt-4", label: "GPT-4" },
          { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
          { value: "gpt-4o", label: "GPT-4o" },
          { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
        ];
      case "ANTHROPIC":
        return [
          { value: "claude-3-opus-20240229", label: "Claude 3 Opus" },
          { value: "claude-3-sonnet-20240229", label: "Claude 3 Sonnet" },
          { value: "claude-3-haiku-20240307", label: "Claude 3 Haiku" },
        ];
      case "GOOGLE":
        return [
          { value: "gemini-pro", label: "Gemini Pro" },
          { value: "gemini-pro-vision", label: "Gemini Pro Vision" },
        ];
      default:
        return [];
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Paper sx={{ p: 4 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
        >
          <PromptIcon color="primary" />
          프롬프트 생성
        </Typography>

        <Grid container spacing={3}>
          {/* 기본 정보 */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              기본 정보
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="프롬프트 키 *"
              placeholder="예: GREETING, FAQ_SEARCH"
              value={formData.promptKey}
              onChange={(e) => handleChange("promptKey", e.target.value)}
              error={!!errors.promptKey}
              helperText={
                errors.promptKey || "고유한 프롬프트 식별자를 입력하세요"
              }
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              type="number"
              label="우선순위"
              value={formData.priorityOrder}
              onChange={(e) =>
                handleChange("priorityOrder", parseInt(e.target.value) || 1)
              }
              error={!!errors.priorityOrder}
              helperText={errors.priorityOrder || "낮을수록 높은 우선순위"}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>사용 여부</InputLabel>
              <Select
                value={formData.useYn}
                onChange={(e) => handleChange("useYn", e.target.value as Yn)}
                label="사용 여부"
              >
                <MenuItem value={Yn.Y}>활성</MenuItem>
                <MenuItem value={Yn.N}>비활성</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="프롬프트 내용 *"
              placeholder="AI가 사용할 프롬프트 내용을 입력하세요..."
              value={formData.promptContent}
              onChange={(e) => handleChange("promptContent", e.target.value)}
              error={!!errors.promptContent}
              helperText={
                errors.promptContent ||
                "AI가 참조할 상세한 지시사항을 작성하세요"
              }
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="사용 조건"
              placeholder="예: 태권도 수업 관련 문의, 등록 절차 안내"
              value={formData.useCondition}
              onChange={(e) => handleChange("useCondition", e.target.value)}
              helperText="이 프롬프트가 사용될 조건이나 상황을 설명하세요"
              inputProps={{ maxLength: 200 }}
            />
          </Grid>

          {/* AI 모델 설정 */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              <AiIcon color="primary" sx={{ mr: 1 }} />
              AI 모델 설정
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>AI 모델 제공자</InputLabel>
              <Select
                value={formData.aiModelProvider}
                onChange={(e) => {
                  handleChange("aiModelProvider", e.target.value);
                  // 제공자 변경 시 모델명 초기화
                  const models = getModelOptions();
                  if (models.length > 0) {
                    handleChange("aiModelName", models[0].value);
                  }
                }}
                label="AI 모델 제공자"
              >
                <MenuItem value="OPENAI">OpenAI</MenuItem>
                <MenuItem value="ANTHROPIC">Anthropic</MenuItem>
                <MenuItem value="GOOGLE">Google</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>AI 모델명</InputLabel>
              <Select
                value={formData.aiModelName}
                onChange={(e) => handleChange("aiModelName", e.target.value)}
                label="AI 모델명"
              >
                {getModelOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              type="number"
              label="최대 토큰"
              value={formData.aiMaxTokens}
              onChange={(e) =>
                handleChange("aiMaxTokens", parseInt(e.target.value) || 4000)
              }
              error={!!errors.aiMaxTokens}
              helperText={errors.aiMaxTokens || "AI 응답의 최대 길이"}
              inputProps={{ min: 1, max: 32000 }}
            />
          </Grid>

          <Grid size={12}>
            <Box sx={{ px: 2 }}>
              <Typography gutterBottom>
                온도 (Temperature): {formData.aiModelTemperature}
              </Typography>
              <Slider
                value={formData.aiModelTemperature}
                onChange={(_, value) =>
                  handleChange("aiModelTemperature", value as number)
                }
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: "0.0 (정확)" },
                  { value: 0.7, label: "0.7 (균형)" },
                  { value: 2, label: "2.0 (창의적)" },
                ]}
                valueLabelDisplay="auto"
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  label="정확하고 일관된 답변"
                  size="small"
                  color={
                    formData.aiModelTemperature <= 0.3 ? "primary" : "default"
                  }
                />
                <Chip
                  label="균형잡힌 답변"
                  size="small"
                  color={
                    formData.aiModelTemperature > 0.3 &&
                    formData.aiModelTemperature <= 0.7
                      ? "primary"
                      : "default"
                  }
                />
                <Chip
                  label="창의적이고 다양한 답변"
                  size="small"
                  color={
                    formData.aiModelTemperature > 0.7 ? "primary" : "default"
                  }
                />
              </Stack>
            </Box>
          </Grid>

          {/* 도움말 */}
          <Grid size={12}>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>💡 프롬프트 작성 팁:</strong>
                <br />
                • 명확하고 구체적인 지시사항을 작성하세요
                <br />
                • 예상되는 사용자 질문 유형을 고려하세요
                <br />
                • AI가 일관된 톤앤매너로 답변하도록 가이드라인을 제시하세요
                <br />• 필요시 답변 형식이나 구조를 명시하세요
              </Typography>
            </Alert>
          </Grid>

          {/* 제출 버튼 */}
          <Grid size={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                disabled={isLoading}
              >
                {isLoading ? "생성 중..." : "프롬프트 생성"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

